// Rounding and rebalancing mirror apps/react TableBlock/table-width-utils.ts; keep in sync.
const WIDTH_PRECISION = 6;

const MIN_COLUMN_PERCENT = 8;
const MIN_CELL_DEMAND = 3;
const MAX_CELL_DEMAND = 80;
const EMBEDDED_BLOCK_DEMAND = 12;
const LONGEST_WORD_FACTOR = 1.25;

const round = (value: number) => Number(value.toFixed(WIDTH_PRECISION));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const collectCellText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(collectCellText).join(' ');
  if (!isRecord(value)) return '';
  if (typeof value.text === 'string') return value.text;
  if (Array.isArray(value.children)) return collectCellText(value.children);
  if ('content' in value) return collectCellText(value.content);
  return '';
};

const isEmbeddedBlock = (value: unknown): boolean => {
  if (isRecord(value) && value.kind === 'table_cell_block') return true;
  if (!Array.isArray(value)) return false;
  return value.some(
    (node) =>
      isRecord(node) && typeof node.type === 'string' && node.type.startsWith('table-cell-'),
  );
};

const getCellDemand = (value: unknown): number => {
  if (isEmbeddedBlock(value)) return EMBEDDED_BLOCK_DEMAND;
  if (isRecord(value) && isEmbeddedBlock(value.content)) return EMBEDDED_BLOCK_DEMAND;

  const text = collectCellText(value).replace(/\s+/g, ' ').trim();
  if (!text) return MIN_CELL_DEMAND;

  const longestWord = text.split(' ').reduce((longest, word) => Math.max(longest, word.length), 0);
  const demand = Math.max(text.length, longestWord * LONGEST_WORD_FACTOR);

  return Math.min(MAX_CELL_DEMAND, Math.max(MIN_CELL_DEMAND, demand));
};

const rebalance = (widths: number[]): number[] => {
  const rounded = widths.map(round);
  const total = rounded.reduce((sum, width) => sum + width, 0);
  const diff = round(100 - total);

  if (Math.abs(diff) > 0 && rounded.length > 0) {
    const targetIndex = rounded.reduce(
      (maxIndex, width, index, source) => (width > (source[maxIndex] ?? 0) ? index : maxIndex),
      0,
    );
    rounded[targetIndex] = round((rounded[targetIndex] ?? 0) + diff);
  }

  return rounded;
};

// Each column keeps a readable floor; the remaining width is shared in
// proportion to how much text its widest cell needs.
const toPercentWidths = (demands: number[]): number[] => {
  const columnCount = demands.length;
  if (columnCount === 0) return [];

  const equalWidth = 100 / columnCount;
  const minWidth = Math.min(MIN_COLUMN_PERCENT, equalWidth);
  const extras = demands.map((demand) => Math.max(0, demand - MIN_CELL_DEMAND));
  const extraTotal = extras.reduce((sum, extra) => sum + extra, 0);

  if (extraTotal <= 0) {
    return rebalance(demands.map(() => equalWidth));
  }

  const available = 100 - minWidth * columnCount;
  const widths = extras.map((extra) => minWidth + (available * extra) / extraTotal);

  return rebalance(widths);
};

export const inferTableColumnWidths = (rows: unknown): number[] => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const columnCount = rows.reduce(
    (max, row) => (Array.isArray(row) ? Math.max(max, row.length) : max),
    0,
  );
  if (columnCount === 0) return [];

  const demands = Array.from({ length: columnCount }, () => MIN_CELL_DEMAND);
  rows.forEach((row) => {
    if (!Array.isArray(row)) return;
    row.forEach((cell, index) => {
      const current = demands[index] ?? MIN_CELL_DEMAND;
      demands[index] = Math.max(current, getCellDemand(cell));
    });
  });

  return toPercentWidths(demands);
};
